import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MapPin, User2, Boxes, Sparkles, Plus, Trash2, Camera, Receipt, ArrowRight, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { photoUrl } from "@/lib/api";
import StatusPill from "@/components/StatusPill";

const fmt = (iso) => new Date(iso).toLocaleString();

// Forward vs backward transitions per current status.
const FORWARD = {
  NEW: [{ to: "SCHEDULED", label: "Mark scheduled" }, { to: "EN_ROUTE", label: "Mark en route" }],
  SCHEDULED: [{ to: "EN_ROUTE", label: "Mark en route" }],
  EN_ROUTE: [{ to: "ON_SITE", label: "Mark on site" }],
  ON_SITE: [{ to: "COMPLETE", label: "Mark complete" }],
  COMPLETE: [{ to: "INVOICED", label: "Move to invoiced" }],
  INVOICED: [{ to: "CLOSED", label: "Close job" }],
  CLOSED: [],
};
const BACKWARD = {
  SCHEDULED: [{ to: "NEW", label: "Move back to New" }],
  EN_ROUTE: [{ to: "SCHEDULED", label: "Move back to Scheduled" }],
  ON_SITE: [{ to: "SCHEDULED", label: "Move back to Scheduled" }],
  COMPLETE: [{ to: "SCHEDULED", label: "Reopen — back to Scheduled" }],
  INVOICED: [{ to: "SCHEDULED", label: "Reopen — back to Scheduled" }],
  CLOSED: [{ to: "SCHEDULED", label: "Reopen — back to Scheduled" }],
};

export default function JobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [showRaw, setShowRaw] = useState({});
  const [noteText, setNoteText] = useState("");
  const [part, setPart] = useState({ name: "", qty: 1, price: 0 });
  const [techs, setTechs] = useState([]);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/jobs/${jobId}`).then((r) => setData(r.data));
  }, [jobId]);

  useEffect(() => {
    load();
    api.get("/techs").then((r) => setTechs(r.data));
  }, [load]);

  if (!data) return <div className="text-[#6A6E85]">Loading…</div>;
  const { job, notes, invoice } = data;

  const setStatus = async (to) => {
    try {
      await api.post(`/jobs/${jobId}/status`, { status: to });
      toast.success(`Status updated`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update status");
    }
  };

  const setStatusBackward = async (to, label) => {
    if (!window.confirm(`Move this job back to ${to.replace("_", " ")}? ${label}.`)) return;
    setStatus(to);
  };

  const assignTech = async (tech_id) => {
    try {
      await api.patch(`/jobs/${jobId}`, { assigned_tech_id: tech_id || null });
      toast.success(tech_id ? "Tech assigned" : "Unassigned");
      load();
    } catch { toast.error("Failed to assign"); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await api.post(`/jobs/${jobId}/notes`, { raw_text: noteText.trim() });
      setNoteText("");
      toast.success("Note added");
      load();
    } catch { toast.error("Could not add note"); }
  };

  const addPart = async () => {
    if (!part.name.trim()) return;
    try {
      await api.post(`/jobs/${jobId}/parts`, {
        name: part.name.trim(),
        qty: Number(part.qty) || 1,
        price: Number(part.price) || 0,
      });
      setPart({ name: "", qty: 1, price: 0 });
      load();
    } catch { toast.error("Could not add part"); }
  };

  const removePart = async (idx) => {
    await api.delete(`/jobs/${jobId}/parts/${idx}`);
    load();
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/jobs/${jobId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded");
      load();
    } catch { toast.error("Upload failed"); }
    finally { e.target.value = ""; }
  };

  const forwards = FORWARD[job.status] || [];
  const backwards = BACKWARD[job.status] || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <button onClick={() => navigate(-1)} className="text-[#6A6E85] hover:text-[#181854] text-sm mb-1">← Back</button>
          <h1 className="text-3xl font-bold text-[#181854] tracking-tight">{job.title}</h1>
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <StatusPill status={job.status} size="lg" />
            <span className="text-sm text-[#6A6E85]">Scheduled {fmt(job.scheduled_at)}</span>
            {job.priority && (
              <span className="text-xs font-semibold uppercase tracking-wider text-[#181854] bg-[#ECEDF6] px-2.5 py-1 rounded-full">{job.priority}</span>
            )}
            {job.source === "maintenance" && (
              <span className="text-xs font-semibold uppercase tracking-wider text-[#4F9CE0] bg-[#E0F2FE] px-2.5 py-1 rounded-full">Maintenance</span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          {forwards.map(({ to, label }) => (
            <button
              key={to}
              onClick={() => setStatus(to)}
              data-testid={`status-set-${to}`}
              className="inline-flex items-center gap-2 bg-[#181854] hover:bg-[#101038] text-white px-4 py-2.5 rounded-lg font-medium transition-colors duration-200"
            >
              {label} <ArrowRight size={14} />
            </button>
          ))}
          {backwards.map(({ to, label }) => (
            <button
              key={`back-${to}`}
              onClick={() => setStatusBackward(to, label)}
              data-testid={`status-back-${to}`}
              className="inline-flex items-center gap-2 bg-[#ECEDF6] hover:bg-[#E4E6EF] text-[#6A6E85] hover:text-[#181854] px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
              title={label}
            >
              <RotateCcw size={13} /> {label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="bg-white rounded-xl border border-[#E4E6EF] p-4 shadow-sm">
              <p className="text-[10px] uppercase tracking-wider text-[#6A6E85] font-semibold">Client</p>
              <p className="text-[#14162E] font-semibold mt-1">{job.client_name}</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E4E6EF] p-4 shadow-sm">
              <p className="text-[10px] uppercase tracking-wider text-[#6A6E85] font-semibold flex items-center gap-1"><MapPin size={11} /> Site</p>
              <p className="text-[#14162E] text-sm mt-1">{job.site_address}</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E4E6EF] p-4 shadow-sm">
              <p className="text-[10px] uppercase tracking-wider text-[#6A6E85] font-semibold flex items-center gap-1"><Boxes size={11} /> Asset</p>
              {job.asset_label ? (
                <p className="text-[#14162E] text-sm mt-1">{job.asset_label} <span className="text-[#6A6E85]">({job.asset_type})</span></p>
              ) : (
                <p className="text-[#6A6E85] text-sm mt-1 italic">No asset linked</p>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-[#E4E6EF] p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-[#181854] flex items-center gap-2"><Sparkles size={18} className="text-[#4F9CE0]" /> Notes</h2>
              <span className="text-xs text-[#6A6E85]">{notes.length} entries</span>
            </div>
            <div className="flex gap-2">
              <input
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Add a note (auto-tidied)…"
                data-testid="note-input"
                className="flex-1 px-3 py-2.5 rounded-lg border border-[#E4E6EF] focus:border-[#4F9CE0] outline-none"
              />
              <button onClick={addNote} data-testid="note-add" className="bg-[#181854] hover:bg-[#101038] text-white px-4 py-2.5 rounded-lg font-medium transition-colors duration-200">Add</button>
            </div>
            <div className="mt-4 space-y-3">
              {notes.length === 0 && <p className="text-sm text-[#6A6E85] py-4 text-center">No notes yet.</p>}
              {notes.map((n) => (
                <div key={n.id} className="bg-[#F5F6FA] rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-[#6A6E85]">{n.author === "tech" ? "Tech" : "Office"} · {fmt(n.created_at)}</span>
                    <button onClick={() => setShowRaw((s) => ({ ...s, [n.id]: !s[n.id] }))} className="text-xs text-[#4F9CE0] hover:underline" data-testid={`note-toggle-${n.id}`}>
                      {showRaw[n.id] ? "Show cleaned" : "Show raw"}
                    </button>
                  </div>
                  <p className="text-sm text-[#14162E] whitespace-pre-wrap">{showRaw[n.id] ? n.raw_text : n.clean_text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-[#E4E6EF] p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-[#181854] flex items-center gap-2"><Camera size={18} /> Photos</h2>
              <div>
                <input ref={fileRef} type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="photo-input" />
                <button onClick={() => fileRef.current?.click()} data-testid="photo-upload-btn" className="text-sm bg-[#ECEDF6] hover:bg-[#E4E6EF] text-[#181854] px-3 py-2 rounded-lg font-medium transition-colors duration-200">Upload</button>
              </div>
            </div>
            {(!job.photos || job.photos.length === 0) ? (
              <p className="text-sm text-[#6A6E85] py-6 text-center">No photos.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {job.photos.map((p) => (
                  <a key={p.id} href={photoUrl(p.id)} target="_blank" rel="noreferrer">
                    <img src={photoUrl(p.id)} alt={p.filename} className="w-full h-32 object-cover rounded-lg border border-[#E4E6EF]" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-[#E4E6EF] p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-[#181854] flex items-center gap-2"><User2 size={18} /> Assigned tech</h2>
            <select value={job.assigned_tech_id || ""} onChange={(e) => assignTech(e.target.value)} data-testid="assign-tech-select" className="mt-3 w-full px-3 py-2.5 rounded-lg border border-[#E4E6EF] focus:border-[#4F9CE0] outline-none bg-white">
              <option value="">Unassigned</option>
              {techs.map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
            </select>
          </div>

          <div className="bg-white rounded-xl border border-[#E4E6EF] p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-[#181854]">Parts used</h2>
            <ul className="mt-3 space-y-2">
              {(job.parts || []).map((p, i) => (
                <li key={i} className="flex items-center justify-between bg-[#F5F6FA] rounded-lg px-3 py-2 text-sm">
                  <div>
                    <div className="text-[#14162E]">{p.name}</div>
                    <div className="text-xs text-[#6A6E85]">{p.qty} × ${Number(p.price).toFixed(2)}</div>
                  </div>
                  <button onClick={() => removePart(i)} className="text-[#DC2626] hover:bg-[#FEE2E2] rounded p-1.5 transition-colors duration-200" aria-label="Remove"><Trash2 size={14} /></button>
                </li>
              ))}
              {(job.parts || []).length === 0 && <li className="text-sm text-[#6A6E85] py-2">No parts recorded.</li>}
            </ul>
            <div className="mt-3 space-y-2">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-[#6A6E85]">Part name</label>
                <input placeholder="e.g. R404A refrigerant (150g)" value={part.name} onChange={(e) => setPart({ ...part, name: e.target.value })} data-testid="part-name" className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E4E6EF] focus:border-[#4F9CE0] outline-none text-sm" />
              </div>
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-3">
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-[#6A6E85]">Qty</label>
                  <input type="number" min="0" step="0.5" value={part.qty} onChange={(e) => setPart({ ...part, qty: e.target.value })} data-testid="part-qty" className="mt-1 w-full px-2 py-2 rounded-lg border border-[#E4E6EF] focus:border-[#4F9CE0] outline-none text-sm" />
                </div>
                <div className="col-span-6">
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-[#6A6E85]">Unit price ($)</label>
                  <div className="mt-1 relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6A6E85] text-sm">$</span>
                    <input type="number" min="0" step="0.01" value={part.price} onChange={(e) => setPart({ ...part, price: e.target.value })} data-testid="part-price" className="w-full pl-6 pr-2 py-2 rounded-lg border border-[#E4E6EF] focus:border-[#4F9CE0] outline-none text-sm" />
                  </div>
                </div>
                <div className="col-span-3">
                  <button onClick={addPart} data-testid="part-add" aria-label="Add part" className="w-full h-[38px] rounded-lg bg-[#181854] hover:bg-[#101038] text-white flex items-center justify-center transition-colors duration-200"><Plus size={16} /></button>
                </div>
              </div>
            </div>
          </div>

          {invoice && (
            <div className="bg-white rounded-xl border border-[#E4E6EF] p-5 shadow-sm">
              <h2 className="text-lg font-semibold text-[#181854] flex items-center gap-2"><Receipt size={18} /> Invoice</h2>
              <div className="mt-3 flex items-center justify-between">
                <div>
                  <div className="text-xs text-[#6A6E85] uppercase tracking-wider font-semibold">{invoice.number || "Status"}</div>
                  <div className="text-[#14162E] font-semibold capitalize">{invoice.status}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-[#6A6E85] uppercase tracking-wider font-semibold">Total</div>
                  <div className="text-[#14162E] font-bold text-lg">${invoice.amount.toFixed(2)}</div>
                </div>
              </div>
              <Link to={`/office/invoices/${invoice.id}/print`} data-testid="job-invoice-view" className="mt-3 block text-center bg-[#ECEDF6] hover:bg-[#E4E6EF] text-[#181854] px-4 py-2 rounded-lg font-medium transition-colors duration-200">View / print →</Link>
              <Link to="/office/invoices" data-testid="job-invoice-link" className="mt-2 block text-center text-sm text-[#4F9CE0] hover:underline">Open in Invoices</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
